import { useEffect, useMemo, useRef, useState } from 'react';
import { Panel, PanelType } from '@fluentui/react';
import {
  FluentProvider,
  Tab,
  TabList,
  Text,
  webLightTheme,
  makeStyles,
  shorthands,
  tokens,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import { FormPreview } from '@moko/ui';
import type { IMetadata } from '../../services/metadataService';
import { getAutoCompleteValues } from '../../services/referenceService';
import { useReferenceLookups } from './useReferenceLookups';
import { getUsers } from '../../services/userService';

interface Breakpoint {
  key: string;
  label: string;
  width: number;
}

// World-standard responsive breakpoints (the common Bootstrap/Tailwind tiers).
const BREAKPOINTS: Breakpoint[] = [
  { key: 'mobile', label: 'Mobile', width: 375 },
  { key: 'tablet', label: 'Tablet', width: 768 },
  { key: 'laptop', label: 'Laptop', width: 1024 },
  { key: 'desktop', label: 'Desktop', width: 1440 },
];

// Width rules:
// - Drag is clamped to [MIN_WIDTH, MAX_WIDTH]. Desktop (the largest preset) is
//   the ceiling and MIN matches the smallest realistic phone (iPhone SE).
// - Below laptop width, preview uses the mobile form layout (tablet included).
const MIN_WIDTH = 320;
const MAX_WIDTH = BREAKPOINTS[BREAKPOINTS.length - 1].width;
const DESKTOP_MIN_WIDTH = 1024;

// Every width belongs to exactly one tier, so a preset is always highlighted:
// Mobile < 600, Tablet 600-1023, Laptop 1024-1439, Desktop >= 1440.
const activeTier = (width: number): string => {
  if (width < 600) return 'mobile';
  if (width < 1024) return 'tablet';
  if (width < 1440) return 'laptop';
  return 'desktop';
};

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    height: '100%',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.gap('12px'),
  },
  widthLabel: {
    color: tokens.colorNeutralForeground3,
    minWidth: '70px',
    textAlign: 'center',
  },
  // The frame plus its drag handle sit side by side; the handle is always
  // visible so resizing is discoverable.
  stage: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    maxWidth: '100%',
    height: '74vh',
  },
  // The frame itself does NOT scroll. A `transform` makes it the containing
  // block for any `position: fixed` descendant (e.g. OtherInfo's multi-step
  // BottomNavBar). Because the containing block doesn't scroll, the fixed bar
  // stays glued to the frame's visible bottom while the inner viewport scrolls
  // underneath it -- exactly how the real app behaves against the page.
  frame: {
    ...shorthands.overflow('hidden'),
    ...shorthands.border('10px', 'solid', tokens.colorNeutralForeground1),
    ...shorthands.borderRadius('20px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    boxSizing: 'border-box',
    minWidth: `${MIN_WIDTH}px`,
    height: '100%',
    transform: 'translateZ(0)',
    position: 'relative',
    // Neutralize the app-shell offsets the nav bar reads, so it sits flush with
    // the frame edges regardless of the real Portal layout.
    '--app-sidebar-width': '0px',
    '--app-bottom-nav-height': '0px',
  },
  // The inner scroller.
  viewport: {
    height: '100%',
    ...shorthands.overflow('auto'),
    ...shorthands.padding('16px'),
  },
  // A real (non-padding) spacer at the end of the scroll content. Browsers drop
  // a scroll container's bottom padding once content overflows, so we reserve
  // clearance for the fixed bottom nav bar with an actual element instead.
  bottomSpacer: {
    height: '32px',
    flexShrink: 0,
  },
  handle: {
    width: '18px',
    marginLeft: '6px',
    cursor: 'ew-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ':hover': { backgroundColor: tokens.colorNeutralBackground3Hover },
    touchAction: 'none',
  },
  handleGrip: {
    width: '4px',
    height: '36px',
    ...shorthands.borderRadius('2px'),
    backgroundColor: tokens.colorNeutralForeground3,
  },
});

interface FormPreviewPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  metadata?: IMetadata;
}

const clampWidth = (value: number): number =>
  Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)));

const FormPreviewPanel = ({
  isOpen,
  onDismiss,
  metadata,
}: FormPreviewPanelProps) => {
  const styles = useStyles();
  const [width, setWidth] = useState<number>(BREAKPOINTS[0].width);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const lookups = useReferenceLookups(
    metadata?.fields,
    metadata?.lookups,
    isOpen
  );
  const previewMetadata = useMemo(
    () => (metadata ? { ...metadata, lookups } : undefined),
    [metadata, lookups]
  );

  const mode: 'mobile' | 'desktop' =
    width < DESKTOP_MIN_WIDTH ? 'mobile' : 'desktop';
  const activeKey = activeTier(width);

  const onTabSelect = (_e: SelectTabEvent, data: SelectTabData) => {
    const bp = BREAKPOINTS.find((b) => b.key === data.value);
    if (bp) setWidth(bp.width);
  };

  // Width-only drag: the frame is centered, so it grows/shrinks symmetrically,
  // hence the 2x delta. Listeners live on window so the drag keeps tracking
  // even if the pointer leaves the small handle.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragRef.current) return;
      const delta = (e.clientX - dragRef.current.startX) * 2;
      setWidth(clampWidth(dragRef.current.startWidth + delta));
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const startDrag = (e: React.PointerEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: width };
  };

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={false}
      type={PanelType.smallFluid}
      headerText="Form preview"
      closeButtonAriaLabel="Close"
    >
      <FluentProvider theme={webLightTheme}>
        <div className={styles.body}>
          <div className={styles.toolbar}>
            <TabList selectedValue={activeKey} onTabSelect={onTabSelect}>
              {BREAKPOINTS.map((b) => (
                <Tab key={b.key} value={b.key}>
                  {b.label}
                </Tab>
              ))}
            </TabList>
            <Text className={styles.widthLabel}>{width}px</Text>
          </div>

          <div className={styles.stage}>
            <div className={styles.frame} style={{ width }}>
              <div className={styles.viewport}>
                {previewMetadata ? (
                  <>
                    <FormPreview
                      metadata={previewMetadata}
                      mode={mode}
                      onFetchUsers={getUsers}
                      onFetchAutoComplete={getAutoCompleteValues}
                    />
                    <div className={styles.bottomSpacer} aria-hidden />
                  </>
                ) : null}
              </div>
            </div>
            <div
              className={styles.handle}
              onPointerDown={startDrag}
              role="separator"
              aria-orientation="vertical"
              aria-label="Drag to resize preview"
              title="Drag to resize"
            >
              <div className={styles.handleGrip} />
            </div>
          </div>
        </div>
      </FluentProvider>
    </Panel>
  );
};

export default FormPreviewPanel;
